import React, { useState } from 'react';
import { motion } from 'framer-motion';
const flagshipBrands = [{
  name: 'PMC',
  desc: 'Reference audio systems',
  color: '#6366F1',
  textColor: 'text-[#6366F1]',
  bgClass: 'bg-[#6366F1]'
}, {
  name: 'REL',
  desc: 'Precision sub-bass systems',
  color: '#EC4899',
  textColor: 'text-[#EC4899]',
  bgClass: 'bg-[#EC4899]'
}, {
  name: 'Crestron',
  desc: 'Control architecture',
  color: '#F59E0B',
  textColor: 'text-[#F59E0B]',
  bgClass: 'bg-[#F59E0B]'
}, {
  name: 'Meyer Sound',
  desc: 'Experiential audio',
  color: '#10B981',
  textColor: 'text-[#10B981]',
  bgClass: 'bg-[#10B981]',
  note: '(select projects)'
}];
type EcoBrand = {
  name: string;
  desc: string;
  usedIn?: string;
};
type EcoCategory = {
  title: string;
  color: string;
  brands: EcoBrand[];
};
const ecosystem: EcoCategory[] = [{
  title: 'High-Fidelity & Listening',
  color: '#8B5CF6',
  brands: [{
    name: 'McIntosh',
    desc: 'Iconic amplification & heritage audio',
    usedIn: 'Listening Rooms'
  }, {
    name: 'Luxman',
    desc: 'Pure analog refinement',
    usedIn: 'Audiophile Spaces'
  }, {
    name: 'Devialet',
    desc: 'High-performance hybrid audio',
    usedIn: 'Living Spaces'
  }, {
    name: 'Rotel',
    desc: 'Precision electronics',
    usedIn: 'Multi-Room'
  }, {
    name: 'Wharfedale',
    desc: 'Classic British loudspeakers',
    usedIn: 'Listening Rooms'
  }]
}, {
  title: 'Architectural & Distributed Audio',
  color: '#22D3EE',
  brands: [{
    name: 'Sonance',
    desc: 'Architectural audio solutions',
    usedIn: 'Whole Home'
  }, {
    name: 'Amina',
    desc: 'Invisible speakers',
    usedIn: 'Living & Cinema'
  }, {
    name: 'Proficient Audio',
    desc: 'Distributed audio systems',
    usedIn: 'Multi-Room'
  }, {
    name: 'Sonos',
    desc: 'Wireless multi-room audio',
    usedIn: 'Casual Zones'
  }]
}, {
  title: 'Immersive & Performance Audio',
  color: '#F97316',
  brands: [{
    name: 'K-array',
    desc: 'Compact high-performance systems',
    usedIn: 'Outdoor & Events'
  }, {
    name: 'Void Acoustics',
    desc: 'Design-led performance audio',
    usedIn: 'Lounge & Club'
  }, {
    name: 'CSC Audio',
    desc: 'Custom cinema solutions',
    usedIn: 'Cinema'
  }, {
    name: 'Byford Audio',
    desc: 'Specialist audio systems',
    usedIn: 'Performance'
  }]
}, {
  title: 'Cinema & Video Processing',
  color: '#6366F1',
  brands: [{
    name: 'Trinnov',
    desc: 'Advanced room processing',
    usedIn: 'Cinema'
  }, {
    name: 'Arcam',
    desc: 'Cinema electronics',
    usedIn: 'Cinema'
  }, {
    name: 'StormAudio',
    desc: 'Immersive audio processing',
    usedIn: 'Cinema'
  }, {
    name: 'Lumagen',
    desc: 'Video processing excellence',
    usedIn: 'Cinema'
  }, {
    name: 'SIM2',
    desc: 'Reference projection systems',
    usedIn: 'Cinema'
  }]
}, {
  title: 'Amplification & Signal',
  color: '#EC4899',
  brands: [{
    name: 'Powersoft',
    desc: 'High-efficiency amplification',
    usedIn: 'Performance'
  }, {
    name: 'Blustream',
    desc: 'AV distribution',
    usedIn: 'Infrastructure'
  }]
}, {
  title: 'Infrastructure & Control',
  color: '#3B82F6',
  brands: [{
    name: 'Basalte',
    desc: 'Design-led control interfaces',
    usedIn: 'Living Spaces'
  }, {
    name: 'KNX',
    desc: 'Open automation standard',
    usedIn: 'Whole Home'
  }]
}, {
  title: 'Security & Access',
  color: '#64748B',
  brands: [{
    name: 'Ajax',
    desc: 'Smart security systems',
    usedIn: 'Security'
  }, {
    name: 'ekey',
    desc: 'Biometric access control',
    usedIn: 'Entry & Access'
  }]
}];
function EcoTile({
  brand,
  color
}: {
  brand: EcoBrand;
  color: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return <motion.div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="bg-carbon/60 border border-graphite rounded-lg px-5 py-4 relative overflow-hidden group cursor-default transition-all duration-300" style={{
    borderColor: isHovered ? `${color}50` : undefined,
    boxShadow: isHovered ? `0 0 20px ${color}15` : 'none'
  }} whileHover={{
    y: -2
  }} transition={{
    duration: 0.2
  }} data-id="element-119">
      <div className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-60 transition-opacity duration-500" style={{
      backgroundColor: color
    }} data-id="element-120" />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500" style={{
      backgroundColor: color
    }} data-id="element-121" />

      <div className="relative z-10" data-id="element-122">
        <h4 className="font-serif text-lg mb-0.5 transition-colors duration-300" style={{
        color: isHovered ? color : '#E8E4DE'
      }} data-id="element-123">
          {brand.name}
        </h4>
        <p className={`text-xs transition-colors duration-300 ${isHovered ? 'text-silver' : 'text-ash'}`} data-id="element-124">
          {brand.desc}
        </p>
        {brand.usedIn && isHovered && <motion.p initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-[10px] tracking-widest uppercase mt-2" style={{
        color: `${color}99`
      }} data-id="element-125">
            Used in {brand.usedIn}
          </motion.p>}
      </div>
    </motion.div>;
}
export function BrandLayer() {
  return <section className="py-32 bg-obsidian relative border-t border-graphite" data-id="element-126">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-127">
        {/* Flagship Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-128">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-129">
            The world's best technology. Thoughtfully chosen.
          </h2>
          <p className="text-silver max-w-2xl mx-auto text-lg" data-id="element-130">
            We work with some of the most respected names in sound, control, and
            immersive technology — integrated into a single seamless system.
          </p>
        </motion.div>

        {/* Flagship Brands Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-131">
          {flagshipBrands.map((brand, idx) => <motion.div key={brand.name} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1,
          duration: 0.6
        }} className="bg-carbon border border-graphite rounded-xl p-8 text-center relative overflow-hidden group hover:border-silver/30 transition-colors" data-id="element-132">
              <div className={`absolute top-0 left-0 right-0 h-0.5 ${brand.bgClass} opacity-70`} data-id="element-133" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 blur-xl" style={{
            backgroundColor: brand.color
          }} data-id="element-134" />
              <h3 className={`text-title font-serif mb-2 ${brand.textColor} tracking-wide`} data-id="element-135">
                {brand.name}
              </h3>
              <p className="text-silver text-sm" data-id="element-136">{brand.desc}</p>
              {brand.note && <p className="text-ash text-xs mt-1 italic" data-id="element-137">{brand.note}</p>}
            </motion.div>)}
        </div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="text-ash text-sm italic text-center mb-32" data-id="element-138">
          Brands appear as validation of our standards, never as the headline.
        </motion.p>

        {/* ─── Curated Technology Ecosystem ─── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-20" data-id="element-139">
          <p className="text-silver tracking-[0.3em] uppercase text-xs mb-4" data-id="element-140">
            Beyond the core
          </p>
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-141">
            Curated Technology Ecosystem
          </h2>
          <p className="text-silver max-w-2xl mx-auto" data-id="element-142">
            Beyond our core systems, we work with a curated selection of global
            brands — each chosen for a specific role within a project.
          </p>
        </motion.div>

        {/* Ecosystem Categories */}
        <div className="space-y-16" data-id="element-143">
          {ecosystem.map((category, catIdx) => <motion.div key={category.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-5%'
        }} transition={{
          duration: 0.6
        }} data-id="element-144">
              {/* Category Header */}
              <div className="flex items-center mb-6" data-id="element-145">
                <div className="w-1.5 h-1.5 rounded-full mr-3 shrink-0" style={{
              backgroundColor: category.color
            }} data-id="element-146" />
                <h3 className="text-sm tracking-[0.2em] uppercase font-medium" style={{
              color: category.color
            }} data-id="element-147">
                  {category.title}
                </h3>
                <div className="flex-1 h-px bg-graphite ml-4" data-id="element-148" />
              </div>

              {/* Brand Tiles */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-id="element-149">
                {category.brands.map((brand, brandIdx) => <motion.div key={brand.name} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: brandIdx * 0.05,
              duration: 0.4
            }} data-id="element-150">
                    <EcoTile brand={brand} color={category.color} data-id="element-151" />
                  </motion.div>)}
              </div>
            </motion.div>)}
        </div>

        {/* Closing Statement */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="text-center mt-24 pt-16 border-t border-graphite" data-id="element-152">
          <p className="text-platinum font-serif text-xl mb-2 italic" data-id="element-153">
            And many more — selected per project, without limitation.
          </p>
          <p className="text-ash text-sm" data-id="element-154">
            We work with the best technology available, not just what's
            standard.
          </p>
        </motion.div>
      </div>
    </section>;
}